import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown } from "lucide-react";
import { useState } from "react";

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  const faqs: FAQItem[] = [
    // General FAQs
    {
      category: "general",
      question: "What is AirArrange?",
      answer:
        "AirArrange is a platform that connects workspace owners with businesses and individuals looking for flexible workspace solutions. Whether you need a retail shopfront, industrial workshop, storage space, or office, we help you find and book the perfect space.",
    },
    {
      category: "general",
      question: "How does AirArrange work?",
      answer:
        "Tenants can browse available workspaces, view photos and details, and submit booking requests. Hosts list their spaces with pricing and availability. Once a booking is approved, payment is processed securely through our platform.",
    },
    {
      category: "general",
      question: "Is AirArrange available in my area?",
      answer:
        "AirArrange is expanding across multiple regions. Use our search feature to see available workspaces in your desired location. If we're not in your area yet, sign up for notifications when we expand to your region.",
    },

    // Booking FAQs
    {
      category: "booking",
      question: "How do I make a booking?",
      answer:
        "Browse workspaces using our search filters, select your desired dates, review the pricing and terms, and submit a booking request. The host will review your request and either approve or decline. Once approved, you'll complete payment through our secure system.",
    },
    {
      category: "booking",
      question: "Can I cancel my booking?",
      answer:
        "Cancellation policies vary by host. Each listing displays the specific cancellation policy. Generally, cancellations made well in advance may receive a full or partial refund, while last-minute cancellations may incur fees.",
    },
    {
      category: "booking",
      question: "What happens if my booking request is declined?",
      answer:
        "If a host declines your request, no charges are made. You're free to submit requests for other workspaces. We recommend having alternative options ready, especially during busy periods.",
    },
    {
      category: "booking",
      question: "Can I extend my booking?",
      answer:
        "Yes, if the space is available. Contact your host through our messaging system to request an extension. The host will confirm availability and updated pricing, and you can process the extension through your booking dashboard.",
    },

    // Payment FAQs
    {
      category: "payment",
      question: "When do I pay for my booking?",
      answer:
        "Payment is processed after the host approves your booking request. You'll receive a notification to complete payment. Your booking is confirmed once payment is successfully processed.",
    },
    {
      category: "payment",
      question: "What payment methods do you accept?",
      answer:
        "We accept all major credit cards, debit cards, and digital payment methods. All transactions are processed through our secure payment gateway with industry-standard encryption.",
    },
    {
      category: "payment",
      question: "Are there any additional fees?",
      answer:
        "AirArrange charges a service fee to tenants (typically 10-15% of the booking subtotal) to cover platform costs. Some hosts may also charge cleaning fees or security deposits. All fees are clearly displayed before you confirm your booking.",
    },
    {
      category: "payment",
      question: "When do hosts receive payment?",
      answer:
        "Hosts receive payment 24 hours after the tenant's move-in date. This ensures the tenant has moved in successfully. Payments are transferred to the host's registered bank account or payment method.",
    },

    // Host FAQs
    {
      category: "hosting",
      question: "How much does it cost to list my workspace?",
      answer:
        "Listing your workspace on AirArrange is completely free. We only charge a host service fee (typically 3-5%) when you receive a booking. There are no upfront costs or monthly fees.",
    },
    {
      category: "hosting",
      question: "How do I set my pricing?",
      answer:
        "You have full control over your pricing. You can set different rates for hourly, daily, weekly, or monthly bookings. We provide pricing suggestions based on similar spaces in your area, but the final decision is yours.",
    },
    {
      category: "hosting",
      question: "Can I decline a booking request?",
      answer:
        "Yes, you can decline any booking request. However, frequent declinations may affect your listing's visibility. We recommend keeping your calendar updated to avoid receiving requests for unavailable dates.",
    },
    {
      category: "hosting",
      question: "What insurance protection do I have as a host?",
      answer:
        "AirArrange offers Host Protection which covers property damage caused by tenants up to $1,000,000. However, we recommend maintaining your own liability and property insurance as our protection has certain exclusions.",
    },

    // Account FAQs
    {
      category: "account",
      question: "How do I create an account?",
      answer:
        "Click 'Sign Up' in the top right corner. You can register using your email address or social media accounts. Complete your profile with business details to help hosts or tenants understand your needs.",
    },
    {
      category: "account",
      question: "Can I have both a tenant and host account?",
      answer:
        "Yes! Your single AirArrange account can be used for both booking workspaces and hosting your own spaces. Simply toggle between tenant and host modes in your account dashboard.",
    },
    {
      category: "account",
      question: "How do I verify my account?",
      answer:
        "Account verification involves confirming your email address and phone number. For enhanced trust, you can also verify your government-issued ID and business documentation. Verified accounts receive higher visibility and trust scores.",
    },
    {
      category: "account",
      question: "I forgot my password. What should I do?",
      answer:
        "Click 'Login' then 'Forgot Password'. Enter your registered email address, and we'll send you a password reset link. Follow the link to create a new password for your account.",
    },
  ];

  const categories = [
    { id: "all", label: "All Questions" },
    { id: "general", label: "General" },
    { id: "booking", label: "Booking" },
    { id: "payment", label: "Payment" },
    { id: "hosting", label: "Hosting" },
    { id: "account", label: "Account" },
  ];

  const filteredFAQs =
    selectedCategory === "all"
      ? faqs
      : faqs.filter((faq) => faq.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[var(--secondary)] text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/help"
            className="inline-flex items-center gap-2 text-white hover:text-gray-200 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Help Centre
          </Link>
          <h1 className="text-4xl font-bold">Frequently Asked Questions</h1>
          <p className="text-gray-200 mt-2">
            Find answers to common questions about AirArrange
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto py-12 px-4">
        {/* Category Filter */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-8">
          <h2 className="text-lg font-semibold mb-4">Filter by Category</h2>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  selectedCategory === category.id
                    ? "bg-[var(--secondary)] text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {/* FAQ List */}
        <div className="bg-white rounded-2xl p-8 shadow-sm">
          <div className="space-y-4">
            {filteredFAQs.map((faq, index) => (
              <div
                key={index}
                className="border-b border-gray-200 pb-4 last:border-0"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full flex items-center justify-between text-left py-2 hover:text-[var(--secondary)] transition"
                >
                  <span className="text-lg font-semibold pr-4">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 flex-shrink-0 transition-transform ${
                      openIndex === index ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openIndex === index && (
                  <div className="mt-3 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-8 bg-blue-50 rounded-2xl p-6 text-center">
          <h3 className="text-xl font-bold mb-2">Still have questions?</h3>
          <p className="text-gray-600 mb-4">
            Our support team is here to help you
          </p>
          <Link
            to="/contactUs"
            className="inline-block bg-[var(--secondary)] text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition"
          >
            Contact Support
          </Link>
        </div>
      </main>
    </div>
  );
}
